import { memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatCircleIcon, HouseIcon, StudentIcon } from "@phosphor-icons/react";
import { APPLICATION_ROUTES } from "../../utils/routes";
import { HeaderBibleIcon } from "../../assets/icons/header_icons";
import { useAppContext } from "../../../app_context";
import { useUserDataContext, hasAnyAdminAccess } from "../../../user_data_context";
import { useCometChatUnread } from "../../../cometchat_unread_context";
import { useCometChatAuth } from "../../../cometchat_auth_context";
import useWindowBreakpoint from "../../../hooks/use_window_breakpoint";
import { useOnlineStatus } from "../../../hooks/use_online_status";
import DesktopSidebar from "./desktop_sidebar";
import TopHeader from "./top_header";
import { useNavSections } from "./use_nav_sections";
import type { MenuItem } from "./nav_item.types";
import lazyWithReload from "../../utils/lazy_with_reload";

// Opened on demand; it carries the avatar cropper, push setup and the ask-question modal.
const SettingsModal = lazyWithReload(() => import("./settings_modal"));
const SupportModal = lazyWithReload(() => import("../modal_windows/support_modal"));

// Phosphor carries the glyphs the designer's prototype uses. Where it has no
// counterpart — the Bible mark, mentorship — our own icon stays.
const NAV_LINKS: MenuItem[] = [
	{
		key: "home",
		path: APPLICATION_ROUTES.HOME,
		icon: HouseIcon,
		textKey: "app-header.menu.home",
	},
	{
		key: "study",
		path: APPLICATION_ROUTES.STUDY,
		icon: StudentIcon,
		textKey: "app-header.menu.study",
	},
	{
		key: "bible",
		path: APPLICATION_ROUTES.BIBLE,
		icon: HeaderBibleIcon,
		textKey: "app-header.menu.bible",
	},
	{
		key: "chats",
		path: APPLICATION_ROUTES.CHATS,
		icon: ChatCircleIcon,
		textKey: "app-header.menu.chats",
	},
];

interface Props {
	forceTopHeader?: boolean;
}

const Header = ({ forceTopHeader = false }: Props) => {
	const { setIsAppMenuExpanded } = useAppContext();
	const { accessRights, unreadAnswersCount, userData } = useUserDataContext();
	const { total: unreadMessages } = useCometChatUnread();
	const { isUiKitInitialized, loggedInUser } = useCometChatAuth();

	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
	const [settingsModalInitialView, setSettingsModalInitialView] = useState<string | null>(null);
	const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
	const openSupportModal = useCallback(() => setIsSupportModalOpen(true), []);
	const closeSupportModal = useCallback(() => setIsSupportModalOpen(false), []);

	const isTablet = useWindowBreakpoint(1024);
	const isOnline = useOnlineStatus();

	// Only the Bible pages have any offline caching (see the offline-PWA cycle) — every
	// other destination needs a live network call the moment it opens, so it's disabled
	// rather than opening onto a broken/blank screen.
	const isNavItemDisabled = useCallback(
		(menuItem: Pick<MenuItem, "path">) => {
			if (isOnline) return false;
			const path = menuItem.path;
			return path !== APPLICATION_ROUTES.BIBLE && !path?.startsWith(`${APPLICATION_ROUTES.BIBLE}/`);
		},
		[isOnline],
	);

	const showChatButton = isUiKitInitialized && !!loggedInUser;
	const showAdminButton = hasAnyAdminAccess(accessRights);

	const openSettingsModal = useCallback((view?: string) => {
		setSettingsModalInitialView(view || null);
		setIsSettingsModalOpen(true);
	}, []);

	const openSettingsModalPlain = useCallback(() => openSettingsModal(), [openSettingsModal]);

	const closeSettingsModal = useCallback(() => {
		setIsSettingsModalOpen(false);
		setSettingsModalInitialView(null);
	}, []);

	// Leaving the tablet width re-opens the bar, which the user may have collapsed there;
	// the desktop sidebar has no equivalent hidden state. Skipped on the first run so a
	// desktop session doesn't get the write for free.
	const isTabletInitialRef = useRef(true);
	useEffect(() => {
		if (isTabletInitialRef.current) {
			isTabletInitialRef.current = false;
			return;
		}
		if (!isTablet) {
			setIsAppMenuExpanded(true);
		}
	}, [isTablet, setIsAppMenuExpanded]);

	const navItems = useMemo(
		() =>
			NAV_LINKS.filter(link => {
				if (link.path === APPLICATION_ROUTES.CHATS) {
					return accessRights.isSuperadmin || showChatButton;
				}
				return true;
			}),
		[accessRights.isSuperadmin, showChatButton],
	);

	const { profileItems, adminItems } = useNavSections(accessRights, !!userData?._id);

	const settingsModal = isSettingsModalOpen && (
		<Suspense fallback={null}>
			<SettingsModal open onClose={closeSettingsModal} initialView={settingsModalInitialView} />
		</Suspense>
	);

	const supportModal = isSupportModalOpen && (
		<Suspense fallback={null}>
			<SupportModal onClose={closeSupportModal} />
		</Suspense>
	);

	if (!isTablet && !forceTopHeader) {
		return (
			<>
				<DesktopSidebar
					navItems={navItems}
					profileItems={profileItems}
					adminItems={adminItems}
					unreadMessages={unreadMessages}
					unreadAnswersCount={unreadAnswersCount}
					isNavItemDisabled={isNavItemDisabled}
					onOpenSettings={openSettingsModalPlain}
					onOpenSupport={openSupportModal}
				/>
				{settingsModal}
				{supportModal}
			</>
		);
	}

	return (
		<>
			<TopHeader
				navItems={navItems}
				unreadMessages={unreadMessages}
				unreadAnswersCount={unreadAnswersCount}
				showAdminButton={showAdminButton}
				isNavItemDisabled={isNavItemDisabled}
				onOpenSettings={openSettingsModal}
				onOpenSupport={openSupportModal}
				isSettingsModalOpen={isSettingsModalOpen}
				forceTopHeader={forceTopHeader}
			/>
			{settingsModal}
			{supportModal}
		</>
	);
};

export default memo(Header);
